import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, LogOut, Users, Camera, Search, ImageOff, Trash2, X } from "lucide-react";
import api, { clearSession, getToken } from "@/lib/api";
import { formatLastFirst } from "@/lib/utils";
import { toast } from "sonner";

export default function AdminStaffCheckIn() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("coverage");
  const [status, setStatus] = useState(null);
  const [missing, setMissing] = useState([]);
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // ----- Day-1 reset modal -----
  const [resetOpen, setResetOpen] = useState(false);
  const [resetPasscode, setResetPasscode] = useState("");
  const [resetIncludeForms, setResetIncludeForms] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);

  // ----- Surgical date-range clear modal -----
  const todayIso = new Date().toISOString().slice(0, 10);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [rangeStart, setRangeStart] = useState(todayIso);
  const [rangeEnd, setRangeEnd] = useState(todayIso);
  const [rangePasscode, setRangePasscode] = useState("");
  const [rangeIncludeLeaveEarly, setRangeIncludeLeaveEarly] = useState(true);
  const [rangeBusy, setRangeBusy] = useState(false);
  const runRangeClear = async () => {
    if (!rangePasscode.trim()) { toast.error("Reset passcode required"); return; }
    if (!rangeStart || !rangeEnd) { toast.error("Pick a start and end date"); return; }
    if (rangeEnd < rangeStart) { toast.error("End date must be on/after start date"); return; }
    setRangeBusy(true);
    try {
      const r = await api.post("/admin/staff-checkin/clear-attendance-range", {
        confirm_passcode: rangePasscode.trim(),
        start_date: rangeStart,
        end_date: rangeEnd,
        clear_leave_early_requests: rangeIncludeLeaveEarly,
      });
      const extra = r.data.deleted_early_departure_requests
        ? ` · ${r.data.deleted_early_departure_requests} leave-early`
        : "";
      toast.success(`Cleared ${r.data.deleted_crew_daily_signins} sign-ins${extra}`);
      setRangeOpen(false);
      setRangePasscode("");
      loadStatus();
      if (tab === "today") loadToday();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    } finally { setRangeBusy(false); }
  };
  const runReset = async () => {
    if (!resetPasscode.trim()) { toast.error("Reset passcode required"); return; }
    setResetBusy(true);
    try {
      const r = await api.post("/admin/staff-checkin/clear-kiosk-data", {
        confirm_passcode: resetPasscode.trim(),
        clear_signin_history: true,
        clear_photos: true,
        clear_form_submissions: resetIncludeForms,
      });
      toast.success(`Cleared ${r.data.deleted_crew_daily_signins} sign-ins · ${r.data.cleared_photos_count} photos`);
      setResetOpen(false);
      setResetPasscode("");
      setResetIncludeForms(false);
      loadStatus();
      if (tab === "coverage") loadMissing(); else if (tab === "today") loadToday();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setResetBusy(false); }
  };

  useEffect(() => { if (!getToken()) navigate("/"); }, [navigate]);

  const loadStatus = useCallback(async () => {
    try {
      const r = await api.get("/admin/staff-checkin/photo-status");
      setStatus(r.data);
    } catch { toast.error("Failed to load status"); }
  }, []);

  const loadMissing = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/staff-checkin/missing-photo");
      setMissing(r.data.items || []);
    } catch { toast.error("Failed to load missing-photo list"); }
    finally { setLoading(false); }
  }, []);

  const loadToday = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/staff-checkin/today");
      setToday(r.data);
    } catch { toast.error("Failed to load today's check-ins"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => {
    if (tab === "coverage") loadMissing();
    if (tab === "today") loadToday();
  }, [tab, loadMissing, loadToday]);

  const clearPhoto = async (id, name) => {
    if (!confirm(`Clear photo for ${name}? They'll be asked to retake next sign-in.`)) return;
    try {
      await api.post(`/admin/staff-checkin/${id}/clear-photo`);
      toast.success("Photo cleared");
      loadStatus(); loadMissing();
    } catch { toast.error("Failed"); }
  };

  const filteredMissing = search ? missing.filter((m) =>
    (m.name || "").toLowerCase().includes(search.toLowerCase()) ||
    (m.division || "").toLowerCase().includes(search.toLowerCase()) ||
    (m.phone_last_4 || "").includes(search)) : missing;

  const counts = today?.counts_by_method || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-staff-checkin-page">
      <div className="bg-white border-b border-slate-200 px-4 py-3 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto flex items-center gap-3 flex-wrap">
          <button onClick={() => navigate("/dashboard")} className="p-2 rounded-lg hover:bg-slate-100" data-testid="admin-staff-back-btn"><ArrowLeft className="w-5 h-5" /></button>
          <Camera className="w-6 h-6 text-teal-600" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Admin</div>
            <h1 className="text-lg font-display font-bold text-slate-900">Staff Check-In</h1>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => setRangeOpen(true)}
                    className="btn-touch inline-flex items-center gap-2 px-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 hover:bg-amber-100 font-semibold"
                    data-testid="admin-staff-clear-range-btn"
                    title="Delete sign-in records for a specific date range (testing tool)">
              <Trash2 className="w-4 h-4" /> Clear Date Range
            </button>
            <button onClick={() => setResetOpen(true)}
                    className="btn-touch inline-flex items-center gap-2 px-3 rounded-xl bg-red-50 border border-red-200 text-red-700 hover:bg-red-100 font-semibold"
                    data-testid="admin-staff-reset-btn"
                    title="DESTRUCTIVE: clear all sign-in data + photos so staff re-onboard on day 1">
              <Trash2 className="w-4 h-4" /> Day-1 Reset
            </button>
            <button onClick={() => { loadStatus(); tab === "coverage" ? loadMissing() : loadToday(); }}
                    className="btn-touch inline-flex items-center gap-2 px-3 rounded-xl bg-slate-100 hover:bg-slate-200 font-semibold"
                    data-testid="admin-staff-refresh-btn">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button onClick={() => { clearSession(); navigate("/"); }} className="btn-touch inline-flex items-center gap-2 px-3 rounded-xl bg-slate-900 text-white font-semibold">
              <LogOut className="w-4 h-4" /> Lock
            </button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto mt-3 flex gap-1">
          <button onClick={() => setTab("coverage")} className={`px-3 py-1.5 rounded-lg text-sm font-semibold uppercase tracking-[0.1em] ${tab === "coverage" ? "bg-teal-600 text-white shadow" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid="admin-staff-tab-coverage">Photo Coverage</button>
          <button onClick={() => setTab("today")} className={`px-3 py-1.5 rounded-lg text-sm font-semibold uppercase tracking-[0.1em] ${tab === "today" ? "bg-teal-600 text-white shadow" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid="admin-staff-tab-today">Today's Check-Ins</button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 space-y-4">

        {status && (
          <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-4" data-testid="admin-staff-coverage-card">
            <Users className="w-8 h-8 text-teal-600" />
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wide font-bold text-slate-500">Photo coverage</div>
              <div className="text-2xl font-bold text-slate-900"><span data-testid="coverage-with">{status.with_photo}</span> / <span data-testid="coverage-total">{status.total_staff}</span> staff</div>
              <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-teal-500" style={{ width: `${status.coverage_pct}%` }} />
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-teal-700" data-testid="coverage-pct">{status.coverage_pct}%</div>
              <div className="text-xs text-slate-500">{status.without_photo} missing</div>
            </div>
          </div>
        )}

        {tab === "coverage" && (
          <>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter missing by name, division or last-4…"
                       className="w-full pl-10 pr-3 py-2 rounded-xl border border-slate-200 outline-none focus:ring-2 focus:ring-teal-400"
                       data-testid="admin-staff-missing-search" />
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="admin-staff-missing-list">
              {loading ? <div className="p-8 text-center text-slate-500">Loading…</div> : (
                filteredMissing.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    {missing.length === 0 ? "Everyone has a photo. 🎉" : "No matches for that filter."}
                  </div>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {filteredMissing.slice(0, 300).map((m) => (
                      <li key={m.crew_member_id} className="p-3 flex items-center gap-3" data-testid={`missing-row-${m.crew_member_id}`}>
                        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center"><ImageOff className="w-5 h-5 text-slate-400" /></div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-slate-900 truncate">{formatLastFirst(m.name)}</div>
                          <div className="text-xs text-slate-500 truncate">{m.division || "—"} · last-4 {m.phone_last_4 || "—"}</div>
                        </div>
                        <button onClick={() => clearPhoto(m.crew_member_id, m.name)} className="text-xs text-slate-400 hover:text-slate-700" data-testid={`clear-photo-${m.crew_member_id}`}>Clear</button>
                      </li>
                    ))}
                  </ul>
                )
              )}
            </div>
          </>
        )}

        {tab === "today" && today && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="admin-staff-today-counts">
              {[
                { k: "phone_last_4", label: "Phone last-4" },
                { k: "admin_manual", label: "Admin help" },
                { k: "legacy", label: "Other / legacy" },
                { k: "verkada", label: "Verkada (retired)" },
              ].map(({ k, label }) => (
                <div key={k} className="bg-white rounded-2xl border border-slate-200 p-3">
                  <div className="text-[10px] uppercase tracking-wide font-bold text-slate-500">{label}</div>
                  <div className="text-2xl font-bold" data-testid={`count-${k}`}>{counts[k] || 0}</div>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="admin-staff-today-list">
              {today.items.length === 0 ? <div className="p-8 text-center text-slate-500">No check-ins yet today.</div> : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="text-left px-3 py-2">Name</th>
                      <th className="text-left px-3 py-2">Division</th>
                      <th className="text-left px-3 py-2">In</th>
                      <th className="text-left px-3 py-2">Out</th>
                      <th className="text-left px-3 py-2">Method</th>
                      <th className="text-left px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {today.items.map((r) => (
                      <tr key={r.signin_id} className="border-t border-slate-100" data-testid={`today-row-${r.signin_id}`}>
                        <td className="px-3 py-2 font-semibold">{formatLastFirst(r.name)}</td>
                        <td className="px-3 py-2 text-slate-600">{r.division || "—"}</td>
                        <td className="px-3 py-2">{r.sign_in_local_display || "—"}</td>
                        <td className="px-3 py-2">{r.sign_out_local_display || "—"}</td>
                        <td className="px-3 py-2 uppercase text-[10px] tracking-wide text-slate-500">{r.checkin_method || "—"}{r.verified_via ? ` · ${r.verified_via}` : ""}</td>
                        <td className="px-3 py-2">{r.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

      </div>

      {/* Surgical date-range clear modal */}
      {rangeOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !rangeBusy && setRangeOpen(false)} data-testid="clear-range-modal">
          <div className="bg-white rounded-3xl max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-amber-100 flex items-start justify-between bg-amber-50">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-amber-700 font-bold">Testing Tool</div>
                <h3 className="text-lg font-bold text-slate-900">Clear attendance — date range</h3>
              </div>
              <button onClick={() => !rangeBusy && setRangeOpen(false)} className="p-1 rounded hover:bg-amber-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-sm text-slate-700 leading-relaxed">
                Deletes <strong>only</strong> the sign-in records inside the date range below. Photos, planned absences, the staff roster, and attendance outside the range are <strong>kept</strong>. Use this to repeatedly test the kiosk without nuking the whole season.
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Start date</label>
                  <input type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)}
                         className="w-full mt-1 px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:ring-2 focus:ring-amber-400 font-mono"
                         data-testid="clear-range-start" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">End date</label>
                  <input type="date" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)}
                         className="w-full mt-1 px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:ring-2 focus:ring-amber-400 font-mono"
                         data-testid="clear-range-end" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={rangeIncludeLeaveEarly} onChange={(e) => setRangeIncludeLeaveEarly(e.target.checked)} data-testid="clear-range-leave-early" />
                Also delete any leave-early requests for those dates
              </label>
              <div>
                <label className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Reset passcode</label>
                <input type="password" value={rangePasscode} onChange={(e) => setRangePasscode(e.target.value)}
                       placeholder="Required to confirm"
                       className="w-full mt-1 px-4 py-3 rounded-xl border-2 border-amber-200 outline-none focus:ring-2 focus:ring-amber-400 font-mono"
                       data-testid="clear-range-passcode" autoFocus />
              </div>
              <div className="grid grid-cols-2 gap-2 pt-2">
                <button onClick={() => setRangeOpen(false)} disabled={rangeBusy}
                        className="rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold py-3 disabled:opacity-50">
                  Cancel
                </button>
                <button onClick={runRangeClear} disabled={rangeBusy || !rangePasscode}
                        className="rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold py-3 disabled:opacity-50"
                        data-testid="clear-range-confirm-btn">
                  {rangeBusy ? "Clearing…" : "Clear range"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Day-1 Reset modal */}
      {resetOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !resetBusy && setResetOpen(false)} data-testid="day1-reset-modal">
          <div className="bg-white rounded-3xl max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-red-100 flex items-start justify-between bg-red-50">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-red-700 font-bold">Destructive — Day 1 Reset</div>
                <h3 className="text-lg font-bold text-slate-900">Clear all kiosk login data</h3>
              </div>
              <button onClick={() => !resetBusy && setResetOpen(false)} className="p-1 rounded hover:bg-red-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-sm text-slate-700 leading-relaxed">
                This will <strong>wipe all kiosk sign-in history and staff photos</strong> so every staff member re-takes their photo on day 1.
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 space-y-1">
                <div className="font-bold uppercase tracking-[0.12em] text-slate-700">What gets wiped:</div>
                <ul className="list-disc pl-5 space-y-0.5">
                  <li>All <strong>crew_daily_signins</strong> (every sign-in/out record)</li>
                  <li>Every staff member's <strong>photo</strong> + photo timestamps</li>
                  <li>Kiosk login timestamps (<code>last_signed_in_at</code>)</li>
                </ul>
                <div className="font-bold uppercase tracking-[0.12em] text-slate-700 mt-2">Preserved:</div>
                <ul className="list-disc pl-5 space-y-0.5">
                  <li>Staff roster (CampMinder names, phones, divisions)</li>
                  <li>Planned absences</li>
                  <li>Visitors / waivers / offender checks</li>
                </ul>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={resetIncludeForms} onChange={(e) => setResetIncludeForms(e.target.checked)} data-testid="reset-include-forms" />
                Also reset the absence-form "submitted" flag (lets staff re-submit)
              </label>
              <div>
                <label className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Reset passcode</label>
                <input
                  type="password"
                  value={resetPasscode}
                  onChange={(e) => setResetPasscode(e.target.value)}
                  placeholder="Required to confirm"
                  className="w-full mt-1 px-4 py-3 rounded-xl border-2 border-red-200 outline-none focus:ring-2 focus:ring-red-400 font-mono"
                  data-testid="reset-passcode-input"
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-2 pt-2">
                <button onClick={() => setResetOpen(false)} disabled={resetBusy}
                        className="rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold py-3 disabled:opacity-50">
                  Cancel
                </button>
                <button onClick={runReset} disabled={resetBusy || !resetPasscode}
                        className="rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold py-3 disabled:opacity-50"
                        data-testid="reset-confirm-btn">
                  {resetBusy ? "Wiping…" : "Wipe it"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
